import type {
  AgentStatus,
  CallEvent,
  ConnectionStatus,
  EventFrame,
  QueueEvent,
  SnapshotFrame,
} from "@savvy/runtime";
import { create } from "zustand";

/**
 * Live floor state: the snapshot, plus every event since.
 *
 * THE RULE THAT MAKES GHOST CALLS IMPOSSIBLE: applySnapshot REPLACES the maps
 * wholesale. Anything held before the snapshot that the snapshot omits is
 * gone, by construction. There are no timers sweeping for stale entries and no
 * heuristics — the only things that remove a call are a call.ended event and
 * the next snapshot, and a snapshot arrives on every reconnect, which is
 * precisely when ghosts would otherwise appear.
 */

export interface LiveCall {
  uuid: string;
  direction: string;
  from: string;
  to: string;
  /** An agent is actually talking to this caller. */
  bridged: boolean;
  /**
   * The agent completed a transfer and dropped, but the carrier declined to
   * take the call over, so caller↔client stay bridged on OUR switch (BACKLOG
   * §8 fallback mode). Real switch load, but nobody of ours is on it — and the
   * conversation is the client's, not ours. Event-sourced only: a reload loses
   * the mark and the call shows as a plain active call until it ends.
   */
  transferred?: boolean;
  queue?: string;
  agent?: string;
  /** Which client the call is FOR (BACKLOG §12). Event-sourced: absent on
   * snapshot rows (channel lists carry no client) until an event arrives. */
  clientName?: string;
  /**
   * Epoch ms when we first learned of the call. For a snapshot row this is the
   * snapshot's as_of — FreeSWITCH's channel list carries no created time — so
   * elapsed times for pre-existing calls are lower bounds, not lies.
   */
  seenAt: number;
}

export interface LiveState {
  conn: ConnectionStatus;
  connDetail?: string;
  /** Snapshot sections that could not be read; render as unknown, not empty. */
  partial: string[];

  calls: Map<string, LiveCall>;
  agents: Map<string, AgentStatus>;
  /** ext → call uuid. Maintained on apply so no view ever scans calls per agent. */
  callByAgent: Map<string, string>;

  applySnapshot(f: SnapshotFrame): void;
  applyEvent(f: EventFrame): void;
  /** Agent statuses re-read over HTTP (see the poll note in App.tsx). */
  applyAgents(agents: AgentStatus[]): void;
  setConn(c: ConnectionStatus, detail?: string): void;
}

/** mod_callcenter names agents "1010@domain"; every other surface says "1010". */
function bareExt(agent: string | undefined): string {
  if (!agent) return "";
  const i = agent.indexOf("@");
  return i > 0 ? agent.slice(0, i) : agent;
}

export const useLive = create<LiveState>((set, get) => ({
  conn: "connecting",
  partial: [],
  calls: new Map(),
  agents: new Map(),
  callByAgent: new Map(),

  applySnapshot(f) {
    const asOf = Date.parse(f.as_of) || Date.now();
    const calls = new Map<string, LiveCall>();
    for (const c of f.data.calls) {
      calls.set(c.call_uuid, {
        uuid: c.call_uuid,
        direction: c.direction ?? "",
        from: c.from ?? "",
        to: c.to ?? "",
        bridged: c.type === "bridged",
        seenAt: asOf,
      });
    }
    const agents = new Map<string, AgentStatus>();
    for (const a of f.data.agents) agents.set(a.extension, a);

    set({ calls, agents, callByAgent: new Map(), partial: f.partial ?? [] });
  },

  applyEvent(f) {
    const [domain] = f.envelope.event_type.split(".", 1);
    if (domain === "call") {
      applyCall(f.envelope.payload as CallEvent, f.envelope.timestamp, set, get);
    } else if (domain === "queue") {
      applyQueue(f.envelope.payload as QueueEvent, set, get);
    }
    // agent.* events will matter once the orchestrator publishes them; until
    // then agent state arrives via the snapshot and the slow HTTP re-read.
  },

  applyAgents(list) {
    const agents = new Map<string, AgentStatus>();
    for (const a of list) agents.set(a.extension, a);
    set({ agents });
  },

  setConn(conn, connDetail) {
    set({ conn, connDetail });
  },
}));

type Set = (partial: Partial<LiveState>) => void;
type Get = () => LiveState;

function applyCall(ev: CallEvent, ts: string, set: Set, get: Get) {
  const { calls, callByAgent } = get();
  const next = new Map(calls);

  if (ev.type === "ended") {
    next.delete(ev.call_uuid);
    const byAgent = new Map(callByAgent);
    for (const [ext, uuid] of byAgent) {
      if (uuid === ev.call_uuid) byAgent.delete(ext);
    }
    set({ calls: next, callByAgent: byAgent });
    return;
  }

  const existing = next.get(ev.call_uuid);
  const transferred = ev.type === "transferred" ? true : (existing?.transferred ?? false);
  next.set(ev.call_uuid, {
    uuid: ev.call_uuid,
    direction: ev.direction || existing?.direction || "",
    from: ev.from || existing?.from || "",
    to: ev.to || existing?.to || "",
    bridged: ev.type === "bridged" ? true : (existing?.bridged ?? false),
    transferred,
    queue: existing?.queue,
    clientName: ev.client_name || existing?.clientName,
    // A transferred call has no agent of ours on it any more.
    agent: transferred ? undefined : existing?.agent,
    seenAt: existing?.seenAt ?? (Date.parse(ts) || Date.now()),
  });
  if (ev.type === "transferred") {
    const byAgent = new Map(callByAgent);
    for (const [ext, uuid] of byAgent) {
      if (uuid === ev.call_uuid) byAgent.delete(ext);
    }
    set({ calls: next, callByAgent: byAgent });
    return;
  }
  set({ calls: next });
}

function applyQueue(ev: QueueEvent, set: Set, get: Get) {
  const { calls, callByAgent } = get();
  const ext = bareExt(ev.agent);

  // Queue events enrich the caller's entry with which queue it is in and, on
  // bridge, which agent has it. CC-Member-Session-UUID is the CALLER's uuid.
  if (ev.call_uuid) {
    const c = calls.get(ev.call_uuid);
    if (c) {
      const next = new Map(calls);
      const updated: LiveCall = { ...c, queue: ev.queue ?? c.queue };
      if (ev.action === "bridge-agent-start" && ext) {
        updated.agent = ext;
        updated.bridged = true;
      }
      next.set(ev.call_uuid, updated);

      const byAgent = new Map(callByAgent);
      if (ev.action === "bridge-agent-start" && ext) byAgent.set(ext, ev.call_uuid);
      if (ev.action === "bridge-agent-end" && ext) byAgent.delete(ext);
      set({ calls: next, callByAgent: byAgent });
      return;
    }
  }

  // Agent-only events (offering, state changes) update the agent's live state.
  if (ext && ev.agent_state) {
    const agents = get().agents;
    const a = agents.get(ext);
    if (a) {
      const next = new Map(agents);
      next.set(ext, { ...a, state: ev.agent_state });
      set({ agents: next });
    }
  }
}
